import { Activity } from 'lucide-react'
import type { HealthAdapter } from '../hooks/adapters'
import { useNarrow } from '../hooks/useNarrow'
import { useNow } from '../hooks/useNow'
import { C12_PURPOSE, HISTORY_DAYS, LEVEL_LABEL, NARROW_DAYS } from '../model/labels'
import { ComponentCard } from './ComponentCard'
import { HealthBanner } from './HealthBanner'
import { IncidentList } from './IncidentList'

export interface HealthStatusPageProps {
  adapter: HealthAdapter
  /** The clinic's time zone (C-12 and the S-04 tab both show clinic time). */
  timeZone: string
  /** The incident history's page (`?page=`). */
  page: number
  onPage: (page: number) => void
  /** `page` (C-12: the page head) or `tab` (inside S-04). */
  layout?: 'page' | 'tab'
}

/**
 * C-12 System status (UserFlows C-12; Frontend §6.3), also the S-04 Health tab (§7.2): the banner, the two
 * components with their bars (90 days, 30 on narrow screens), the legend, and the incident history (20 per page).
 * The real state comes from #29 / #52 (Architecture P-05); a clinic never connected shows only "Not connected".
 */
export function HealthStatusPage({ adapter, timeZone, page, onPage, layout = 'page' }: HealthStatusPageProps) {
  const health = adapter.useHealth()
  const incidents = adapter.useIncidents(page)
  const narrow = useNarrow()
  const now = useNow()
  const days = narrow ? NARROW_DAYS : HISTORY_DAYS

  const head =
    layout === 'page' ? (
      <div className="page-head">
        <div>
          <h1>System status</h1>
          <p>{C12_PURPOSE}</p>
        </div>
      </div>
    ) : (
      <p className="muted status-tab-purpose">{C12_PURPOSE}</p>
    )

  if (health.isError) {
    return (
      <>
        {head}
        <div className="card">
          <div className="empty" role="alert">
            <Activity aria-hidden />
            <div>Couldn&apos;t load the system status.</div>
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={() => {
                void health.refetch()
              }}
            >
              Retry
            </button>
          </div>
        </div>
      </>
    )
  }
  if (health.isPending || !health.data) {
    return (
      <>
        {head}
        <div className="card">
          <div className="empty" role="status">
            <span className="spinner" aria-hidden />
            <div>Loading the system status…</div>
          </div>
        </div>
      </>
    )
  }

  const data = health.data
  const unmonitored = data.status === 'not_connected' || data.status === 'inactive'
  // "Never" when no day shown has any data; otherwise "more than 2 days ago" (task 11's assumption).
  const never = data.components.every((c) => c.days.every((d) => d.level === 'no_data'))
  const list = incidents.data
  return (
    <>
      {head}
      <HealthBanner health={data} now={now} never={never} />
      {!unmonitored && (
        <>
          <div className="card status-card">
            {data.components.map((c) => (
              <ComponentCard
                key={c.component}
                component={c}
                days={days}
                timeZone={timeZone}
                now={now}
                keeperDown={data.pfKeeperApp === 'down'}
              />
            ))}
            <div className="uptime-legend">
              {(['ok', 'minor', 'major'] as const).map((l) => (
                <span key={l}>
                  <i className={`lv-${l}`} /> {LEVEL_LABEL[l]}
                  {l === 'minor' ? ' (≤ 30 min)' : l === 'major' ? ' (> 30 min)' : ''}
                </span>
              ))}
            </div>
          </div>

          <div className="card status-incidents">
            <div className="card-head">
              <div>
                <h2 className="card-title">Incident history</h2>
                <div className="sub">
                  Every time PF Keeper or the Practice Fusion session went down in the last {HISTORY_DAYS} days
                </div>
              </div>
            </div>
            {incidents.isError ? (
              <div className="empty" role="alert">
                <div>Couldn&apos;t load the incidents.</div>
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  onClick={() => {
                    void incidents.refetch()
                  }}
                >
                  Retry
                </button>
              </div>
            ) : !list ? (
              <div className="empty" role="status">
                <span className="spinner" aria-hidden />
                <div>Loading incidents…</div>
              </div>
            ) : (
              <IncidentList
                items={list.items}
                page={list.page}
                pageSize={list.pageSize}
                total={list.total}
                timeZone={timeZone}
                onPage={onPage}
              />
            )}
          </div>
        </>
      )}
    </>
  )
}
